import { queryKeys } from "@/lib/query-keys";
import messageService from "@/lib/services/messages";
import {
    QueryClient,
    useQuery,
    useQueryClient,
    UseQueryOptions,
} from "@tanstack/react-query";
import { useCallback } from "react";
import { IMessage } from "react-native-gifted-chat";

function normalizeChatMessage(msg: IMessage): IMessage {
    return {
        ...msg,
        createdAt:
            msg.createdAt instanceof Date
                ? msg.createdAt
                : new Date(msg.createdAt),
    };
}

export function invalidateMessageQueries(
    qc: QueryClient,
    bookingId: number,
): void {
    qc.invalidateQueries({ queryKey: queryKeys.messages.byBooking(bookingId) });
}

export function syncChatMessagesFromFetch(
    qc: QueryClient,
    bookingId: number,
    messages: IMessage[],
): void {
    qc.setQueryData(
        queryKeys.messages.byBooking(bookingId),
        messages.map(normalizeChatMessage),
    );
}

// ── Initial message load ──────────────────────────────────────────────────────
// This query loads the history ONCE (or when the cache is stale) and is NOT
// re-triggered on screen focus. Real-time additions come via WebSocket and are
// pushed directly into the cache via `appendChatMessage`.

export function useChatMessages(
    bookingId: number,
    options?: Omit<UseQueryOptions<IMessage[]>, "queryKey" | "queryFn">
) {
    return useQuery({
        queryKey: queryKeys.messages.byBooking(bookingId),
        queryFn: async () => {
            const raw = await messageService.getMessages(bookingId);
            return raw.map((msg: any) => normalizeChatMessage(msg)) as IMessage[];
        },
        enabled: Number.isInteger(bookingId) && bookingId > 0,
        // Messages are loaded once per session; WS keeps them fresh.
        staleTime: Infinity,
        ...options,
    });
}

// ── Helper to append a message into the cache (e.g. from WebSocket) ──────────

export function useAppendChatMessage(bookingId: number | null) {
    const qc = useQueryClient();

    return useCallback((incoming: IMessage | IMessage[]) => {
        if (!bookingId) return;
        const newMessages = Array.isArray(incoming) ? incoming : [incoming];

        qc.setQueryData<IMessage[]>(
            queryKeys.messages.byBooking(bookingId),
            (prev = []) => {
                const normalized = newMessages.map(normalizeChatMessage);
                const existingIds = new Set(prev.map((m) => String(m._id)));
                const unique = normalized.filter(
                    (m) => !existingIds.has(String(m._id)),
                );
                return [...unique, ...prev];
            }
        );
    }, [bookingId, qc]);
}


export function removeOptimisticMessage(qc: QueryClient, msg: IMessage, bookingId: number) {
    qc.setQueryData<IMessage[]>(
        queryKeys.messages.byBooking(bookingId),
        (prev = []) => prev.filter((m) => m._id !== msg._id),
    );
}